import { RedoOutlined, SettingOutlined, UndoOutlined } from "@ant-design/icons";
import { Button, Card, Divider, Flex, Segmented, Space, Switch, Tooltip } from "../../../../../shared/frontend/ui";
import React from "react";
import { spacing } from "../app/design-system";
import type { LocalizedText } from "../app/i18n/i18n";
import { useI18n } from "../app/i18n/useI18n";
import type { TemplateVariable } from "../app/templates/templateVariables";
import { DocumentBlockPalette, type PaletteBlockItem } from "./DocumentBlockPalette";
import { DocumentPreviewFrame } from "./DocumentPreviewFrame";
import {
  activateDocumentBlockCanvas,
  documentBlockTextSource,
  type DocumentBlockCanvasBlock,
  type DocumentBlockDropZone,
} from "./documentBlockCanvas";
import {
  applyInlineFontFamily,
  applyInlineFontSize,
  applyInlineBackgroundColor,
  applyInlineTextColor,
  applyTemplateSource,
  applyTemplateValuePreview,
  createInlineTextTarget,
  executeInlineCommand,
  hasInlineSelection,
  restoreInlineSelection,
  systemDocumentHtmlToValue,
} from "./inlineRichText";
import { LoadingOverlay } from "./LoadingOverlay";
import { DocumentZoomControl } from "./DocumentZoomControl";
import { TemplateVariableToolbar, type TemplateVariableEditorTarget } from "./TemplateVariableToolbar";
import { TextFormatToolbar, type TextFormatMarkKey } from "./TextFormatToolbar";
import { windowHeight } from "../../../../../shared/frontend/theme/displayZoom";

/** Ruhezeit nach der letzten Eingabe, bis der Editor als nicht mehr schreibend gilt. */
const WRITING_IDLE_MS = 500;

/** Ein Baustein, wie diese Oberfläche ihn braucht — unabhängig davon, wo er gespeichert wird. */
export type DocumentEditorBlock = DocumentBlockCanvasBlock & {
  /** Bezeichnung des Bausteintyps für Leiste und Auswahl. */
  label: LocalizedText;
  /** Zeichenformat, das die Leiste anzeigt und setzt. */
  format: {
    bold?: boolean | null;
    italic?: boolean | null;
    underline?: boolean | null;
    fontFamily?: string | null;
    fontSizePt?: number | null;
    textColor?: string | null;
    backgroundColor?: string | null;
    alignment?: number | null;
  };
  /** Ob der Baustein Zeichenformat trägt (ein Trennstrich und ein Bild tragen keines). */
  formattable: boolean;
};

export type DocumentEditorSurface = "letter" | "email" | "whatsapp";

type Props = {
  blocks: DocumentEditorBlock[];
  /** Das vollständige Dokument als HTML — es kommt vom Server, diese Fläche baut es nicht nach. */
  previewHtml: string;
  /** Kanalvorschau ohne Dokument-iframe, etwa das WhatsApp-Profil. */
  customPreview?: React.ReactNode;
  loading?: boolean;
  surface: DocumentEditorSurface;
  /** Angebotene Ausgaben; Einzelkanal-Dokumente zeigen E-Mail und Brief gesperrt als festen Kontext. */
  surfaces: DocumentEditorSurface[];
  onSurfaceChange: (surface: DocumentEditorSurface) => void;
  /**
   * Bedienung unmittelbar neben der Ausgabenwahl — etwa die Seite des Briefes. Sie gehört dorthin,
   * weil sie dieselbe Frage beantwortet: **was** man gerade vor sich hat.
   */
  surfaceExtra?: React.ReactNode;
  /** Wählbare Schriften für die Schriftart-Auswahl. */
  fontOptions?: Array<{ value: string; label: string }>;
  previewValuesEnabled: boolean;
  onPreviewValuesChange: (enabled: boolean) => void;
  /** Rohtext und Beispielwerte für die token-sichere Bearbeitung im Vorschaumodus. */
  previewValueSource?: (blockId: string, field: string | null, footerGroup: number | null) => string | null;
  previewTemplateValues?: Readonly<Record<string, string>>;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  /** Angebotene Variablen der Textebene. */
  variables: TemplateVariable[];
  /** Bausteine, die sich hinzufügen lassen; leer heißt: die Auswahl entfällt. */
  addableBlocks?: PaletteBlockItem[];
  onAddBlock?: (type: string, dropTarget?: { sheet: 1 | 2; targetBlockId?: string | null; placement?: "before" | "after"; zone?: DocumentBlockDropZone }) => void;
  onRemoveBlock?: (blockId: string, page?: 1 | 2) => void;
  onPatchBlock: (blockId: string, patch: Record<string, unknown>) => void;
  /** `zone` sagt, ob am Blattfuß abgelegt wurde statt im Textfluss. */
  onReorder?: (blockId: string, overId: string, placement: "before" | "after", zone: DocumentBlockDropZone) => void;
  onLogoPosition?: (blockId: string, position: "left" | "center" | "right") => void;
  /** Der geschriebene Text eines Bausteins; `footerGroup` nennt die Gruppe des Fußtexts. */
  onTextChange: (blockId: string, value: string, footerGroup: number | null) => void;
  /**
   * Ob gerade im Blatt geschrieben wird.
   *
   * Der Aufrufer holt die Vorschau bei jeder Wertänderung neu vom Server — und tauscht damit
   * mitten im Satz das Blatt aus, in dem geschrieben wird. Wer diese Meldung auswertet, lädt erst
   * nach, wenn die Hand vom Text ist.
   */
  onWritingChange?: (writing: boolean) => void;
  /** Die Einstellungen des Bausteins — der einzige Teil, der sich zwischen den Seiten unterscheidet. */
  renderSettings?: (blockId: string, close: () => void, page?: 1 | 2) => React.ReactNode;
  /** Zusätzliche Bedienelemente in der Kopfzeile, etwa Einstellungen und Speichern. */
  headerExtra?: React.ReactNode;
  /**
   * Bedienelemente der Werkzeugleiste, solange nichts ausgewählt ist — dort gehört hin, was das
   * Dokument als Ganzes betrifft, etwa die Gestaltung der E-Mail-Hülle.
   */
  documentActions?: React.ReactNode;
  /**
   * Kontextbedienung für servergerenderte Dokumentteile, die keine Layoutbausteine sind. Ein
   * Beispiel ist die nur vorübergehend eingeblendete Mustertabelle im Erscheinungsbild-Editor.
   */
  documentContextToolbar?: React.ReactNode;
  /** Ergänzt die gemeinsame Bausteinfläche um aufruferspezifische Dokumentinteraktion. */
  onPreviewDocumentReady?: (
    document: Document,
    clearBlockSelection: () => void,
  ) => void | (() => void);
  /**
   * Werkzeugleiste der Bausteinebene — Lage, Größe und Abstände des gewählten Bausteins.
   * `openSettings` öffnet den Drawer, der als einziger Teil zwischen den Seiten verschieden ist.
   */
  renderBlockToolbar?: (blockId: string, openSettings: () => void, page?: 1 | 2) => React.ReactNode;
  /**
   * Ob dieses Dokument auch gestaltet wird. Ein Textsatz sagt hier `false`: Er ändert Inhalte und
   * blendet Bausteine aus, aber Aufbau und Gestaltung gehören seiner Vorlage. Damit entfallen der
   * Umschalter, die Bausteinleiste, der Ziehgriff und „Baustein hinzufügen".
   */
  allowDesign?: boolean;
  /** Bedienelemente am gewählten Baustein im Textmodus, etwa „auf Vorgabe zurücksetzen". */
  renderTextBlockActions?: (blockId: string, page?: 1 | 2) => React.ReactNode;
  /** Sichtbarkeit eines Bausteins umschalten; ohne Rückruf gibt es das Auge nicht. */
  onToggleVisible?: (blockId: string, visible: boolean, page?: 1 | 2) => void;
  /** Prüft, ob ein Baustein auf dieser Seite platziert werden darf. */
  isBlockAllowed?: (type: string, sheet: 1 | 2) => boolean;
  previewRef?: React.RefObject<HTMLIFrameElement | null>;
};

/**
 * Angebotene Vergrößerungen des Blattes.
 *
 * Feste Stufen statt eines freien Reglers: Man will „ganz sehen" oder „genau sehen", nicht 87 %.
 */
const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2];

/** Höhe der Werkzeugzeile — die Höhe eines Standard-Bedienelements. */
const TOOLBAR_ROW_HEIGHT = 32;

/** Mindesthöhe der Fläche; das Dokument wächst darüber hinaus mit seinem Inhalt. */
const FRAME_MIN_HEIGHT = windowHeight("70vh");

/**
 * Angenommene Luft unter dem Blatt beim ersten Messen: der untere Innenabstand des Seiteninhalts.
 * Was darüber hinaus noch Platz braucht, wird gemessen (siehe `usePreviewViewportHeight`).
 */
const PREVIEW_VIEWPORT_BOTTOM_GAP = spacing.appContentPaddingBottom;

/** Mindesthöhe des Blattfensters, damit die Fläche auch auf sehr flachen Fenstern bedienbar bleibt. */
const PREVIEW_VIEWPORT_MIN_HEIGHT = 240;

/**
 * Höhe des Fensters, in dem das Blatt scrollt: der Rest der Bildschirmhöhe unterhalb der
 * Werkzeugleiste. Damit bleiben Kopfzeile und Werkzeugleiste immer sichtbar — **und** die Seite
 * als Ganzes bleibt so hoch wie der Bildschirm, braucht also keinen eigenen Rollbalken.
 *
 * Gemessen statt gerechnet: Über dem Blatt stehen je nach Seite Zurück-Knopf, Überschrift,
 * Hinweise und eine je nach Fensterbreite ein- oder zweizeilige Werkzeugleiste; darunter liegen
 * Innenabstände mehrerer Rahmen. Ein fester Abzug traf diese Höhe nie.
 */
function usePreviewViewportHeight(ref: React.RefObject<HTMLElement | null>) {
  const [height, setHeight] = React.useState<number | null>(null);
  /**
   * Zuletzt gemessene Luft unter dem Blatt. Ablesbar ist sie nur, solange die Seite überläuft;
   * sobald sie passt, gilt der zuletzt gemessene Wert weiter.
   */
  const bottomGapRef = React.useRef<number>(PREVIEW_VIEWPORT_BOTTOM_GAP);

  React.useLayoutEffect(() => {
    const element = ref.current;
    if (element == null) return;

    const measure = () => {
      const root = document.documentElement;
      /*
        `clientHeight` statt `window.innerHeight`: Letzteres zählt eine waagerechte Rollleiste am
        unteren Fensterrand mit. Die Seite geriete dann um deren Höhe zu hoch — und bekäme genau
        deshalb den senkrechten Rollbalken, den es hier zu vermeiden gilt.
      */
      const viewport = root.clientHeight;
      // Dokumentbezogen statt fensterbezogen: Sonst wanderte die gemessene Höhe mit, sobald die
      // Seite doch einmal gescrollt ist.
      const rect = element.getBoundingClientRect();
      const top = rect.top + window.scrollY;

      /*
        Die Luft unter dem Blatt wird abgelesen statt angenommen: Sie ist der Abstand zwischen der
        Unterkante des Blattes und dem Ende der Seite — Innenabstände mehrerer Rahmen, die von hier
        aus nicht zu überblicken sind. Ablesbar ist das nur, solange die Seite überläuft; passt sie
        bereits, endet die Seite am Bildschirmrand und der Abstand wäre nur die zufällige Restfläche.
      */
      if (root.scrollHeight > viewport) {
        bottomGapRef.current = Math.max(0, root.scrollHeight - (rect.bottom + window.scrollY));
      }

      // Abgerundet: Ein aufgerundeter Bruchteil ist genau das eine Pixel zu viel, das die Seite
      // wieder rollen lässt.
      const next = Math.max(
        PREVIEW_VIEWPORT_MIN_HEIGHT,
        Math.floor(viewport - top - bottomGapRef.current)
      );
      // Nur bei echter Abweichung schreiben — das Setzen der Höhe ändert die Seitenhöhe und meldet
      // sich beim Beobachter zurück.
      setHeight((current) => (current != null && Math.abs(current - next) < 1 ? current : next));
    };

    const remeasureFromScratch = () => {
      // Nach einer Fensteränderung brechen Zeilen anders um; die abgelesene Luft gilt dann nicht mehr.
      bottomGapRef.current = PREVIEW_VIEWPORT_BOTTOM_GAP;
      measure();
    };

    measure();
    /*
      Beobachtet wird der Bereich **über** dem Blatt, nicht `document.body`: Der hat in dieser
      Anwendung `height: 100%` und meldet deshalb nichts, wenn der Inhalt wächst. Der umgebende
      Bereich trägt Zurück-Knopf, Überschrift, Hinweise und Werkzeugleiste — ändert sich dort etwas,
      wandert der Anfang des Blattes, und genau das ist die gesuchte Meldung.
    */
    const observer = new ResizeObserver(measure);
    observer.observe(element.parentElement ?? element);
    observer.observe(element);
    window.addEventListener("resize", remeasureFromScratch);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", remeasureFromScratch);
    };
  }, [ref]);

  return height;
}

/**
 * **Die eine** Oberfläche für Dokumente aus Bausteinen — Erscheinungsbild wie Systemtexte.
 *
 * Beide zeigen dasselbe Blatt, dieselbe Werkzeugleiste, dieselben Handgriffe: Ein einfacher Klick
 * wählt den Baustein und zeigt seine Einstellungen, ein Doppelklick öffnet den Text mit Variablen
 * und Zeichenformat, der Griff verschiebt. Unterschiedlich ist ausschließlich, *wohin* eine
 * Änderung geschrieben wird — in die Bausteine eines Formats oder in die Abweichungen eines
 * Textsatzes — und der Drawer hinter „Einstellungen".
 *
 * Sie war zuvor zweimal vorhanden, in Teilen unterschiedlich, und jede Verbesserung erreichte nur
 * eine der beiden Seiten.
 */
export function DocumentBlockEditor({
  blocks,
  previewHtml,
  customPreview,
  loading = false,
  surface,
  surfaces,
  onSurfaceChange,
  surfaceExtra,
  fontOptions,
  previewValuesEnabled,
  onPreviewValuesChange,
  previewValueSource,
  previewTemplateValues,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  variables,
  addableBlocks,
  onAddBlock,
  onRemoveBlock,
  onPatchBlock,
  onReorder,
  onLogoPosition,
  onTextChange,
  onWritingChange,
  renderSettings,
  headerExtra,
  documentActions,
  documentContextToolbar,
  onPreviewDocumentReady,
  renderBlockToolbar,
  allowDesign = true,
  renderTextBlockActions,
  onToggleVisible,
  isBlockAllowed,
  previewRef,
}: Props) {
  const { text } = useI18n();
  const localFrameRef = React.useRef<HTMLIFrameElement>(null);
  const frameRef = previewRef ?? localFrameRef;
  const previewViewportRef = React.useRef<HTMLDivElement>(null);
  const previewViewportHeight = usePreviewViewportHeight(previewViewportRef);

  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  /**
   * Woran gearbeitet wird. Ein ausdrücklicher Schalter statt eines Doppelklicks: Der Modus sagt
   * jederzeit, was ein Klick bewirkt, und man muss zum Verschieben nicht erst aus dem Text heraus.
   */
  const [designMode, setDesignMode] = React.useState<"design" | "text">("design");
  const customSurfacePreview = customPreview != null;
  const staticPreview = customSurfacePreview;
  const mode = staticPreview ? "design" : allowDesign ? designMode : "text";
  const effectivePreviewValuesEnabled = previewValuesEnabled;
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  /**
   * Die Schreibfläche, in der die Schreibmarke steht — dorthin setzt die Leiste ihre Variablen.
   * Als Zustand und nicht als Ref: Die Werkzeugleiste muss beim Wechsel neu zeichnen.
   */
  const [writable, setWritable] = React.useState<HTMLElement | null>(null);

  /**
   * Vergrößerung des Blattes. Sie wirkt im Dokument selbst (`zoom` am Körper) und nicht als
   * Transformation des Rahmens: So bleiben Klick, Auswahl und Schreibmarke an derselben Stelle,
   * während eine skalierte Fläche die Zeigerkoordinaten verschöbe.
   */
  const [zoom, setZoom] = React.useState(1);
  // Die Vergrößerung wird beim Umschalten gesetzt, nicht in einem Effekt: Sie ist die Folge eines
  // Klicks und nicht ein Zustand, den das Blatt von sich aus nachziehen müsste. Für ein frisch
  // aufgebautes Blatt liest die Fläche sie aus `liveRef` nach.
  const applyZoom = (next: number) => {
    setZoom(next);
    const body = frameRef.current?.contentDocument?.body;
    if (body != null) body.style.zoom = String(next);
  };

  const selected = blocks.find((block) => block.id === selectedId) ?? null;
  const [selectedPage, setSelectedPage] = React.useState<1 | 2>(1);
  // Die Bedienung im iframe wird nur bei einem echten Moduswechsel neu gesetzt. Die gerade
  // gewählte Seite ist dagegen laufender Zustand beim Schreiben; läge sie in `activate`, würde
  // ein Klick auf Seite 2 die Schreibfläche samt Markierung neu aufbauen.
  const selectedPageRef = React.useRef<1 | 2>(selectedPage);
  React.useEffect(() => { selectedPageRef.current = selectedPage; }, [selectedPage]);

  /**
   * Das angezeigte Blatt. Es wird **nicht** ausgetauscht, solange jemand darin schreibt: Der Server
   * liefert nach kurzer Ruhezeit ein neues Dokument, und ein Austausch mitten im Satz nimmt die
   * Schreibmarke mit. Nach dem Verlassen der Schreibfläche zieht die Ansicht nach.
  */
  const [frozenPreview, setFrozenPreview] = React.useState<{
    html: string;
    releaseOnChange: boolean;
  } | null>(null);

  /**
   * Das Ende des Schreibens wird verzögert gemeldet.
   *
   * Beim Neuaufsetzen der Fläche — jeder Moduswechsel, jedes Nachziehen des Blattes — verliert die
   * Schreibfläche für einen Augenblick den Fokus. Ohne diese Verzögerung gälte das als „fertig",
   * und genau in diesem Augenblick lüde der Aufrufer nach und ersetzte den Absatz unter der Hand.
   */
  const onWritingChangeRef = React.useRef(onWritingChange);
  React.useEffect(() => { onWritingChangeRef.current = onWritingChange; }, [onWritingChange]);
  const writingOffTimer = React.useRef<number | undefined>(undefined);
  const reportWriting = React.useCallback((active: boolean) => {
    window.clearTimeout(writingOffTimer.current);
    if (active) {
      onWritingChangeRef.current?.(true);
      return;
    }

    writingOffTimer.current = window.setTimeout(() => onWritingChangeRef.current?.(false), WRITING_IDLE_MS);
  }, []);
  React.useEffect(() => () => window.clearTimeout(writingOffTimer.current), []);

  /**
   * Verlässt die aktive Textbearbeitung sofort und synchron, z. B. beim Umschalten zwischen
   * Design/Text, E-Mail/Brief oder Vorschauwerte an/aus.
   */
  const exitEditing = () => {
    window.clearTimeout(writingOffTimer.current);
    const activeDoc = frameRef.current?.contentDocument;
    if (activeDoc?.activeElement instanceof HTMLElement) {
      activeDoc.activeElement.blur();
    }
    setWritable(null);
    // Nicht erst den alten Serverstand einsetzen und kurz darauf die aktualisierte Antwort: Das
    // wären zwei iframe-Ladevorgänge für eine Aktion. Das bearbeitete Blatt bleibt stehen, bis sich
    // das maßgebliche Vorschau-HTML wirklich ändert.
    setFrozenPreview((current) => current == null ? null : { ...current, releaseOnChange: true });
    onWritingChangeRef.current?.(false);
  };

  // Beide Schalterstellungen laden exakt dasselbe serverseitige Dokument. Nur der unsichtbare
  // Marker erzwingt beim bewussten Umschalten einen Neuaufbau des iframe, damit `activate` entweder
  // die Musterwerte darüberlegt oder wieder den unveränderten Rohtext zeigt.
  const livePreviewHtml = `${previewHtml}<!-- mq-preview-values:${effectivePreviewValuesEnabled ? "on" : "off"} -->`;
  const keepFrozenPreview = frozenPreview != null
    && (!frozenPreview.releaseOnChange || frozenPreview.html === livePreviewHtml);
  const renderedHtml = keepFrozenPreview ? frozenPreview.html : livePreviewHtml;

  /**
   * Die Schreibfläche wird **beim Klick** aus dem Dokument geholt, nicht beim Zeichnen der Leiste:
   * Die Vorschau lädt nach jeder Änderung neu, und ein gemerkter Verweis zeigt dann auf ein
   * Element, das gar nicht mehr auf dem Blatt steht. Das war der Grund, warum das Einfügen mal ging
   * und mal nicht.
   */
  const resolveLiveTarget = React.useCallback((): TemplateVariableEditorTarget | null => {
    if (writable == null) return null;
    // Das Dokument kommt aus der Schreibfläche selbst und nicht über den Rahmen: Es ist dasselbe,
    // aber ohne Zugriff auf eine Ref — und damit ohne Abhängigkeit, die sich nicht angeben lässt.
    const doc = writable.ownerDocument;

    // Der Baustein kommt aus der Schreibfläche, nicht aus der Auswahl — beim Schreiben ist die
    // Auswahl nicht zwingend derselbe Baustein, und ohne Auswahl gäbe es gar kein Ziel.
    const blockId = writable.closest<HTMLElement>("[data-mq-block]")?.getAttribute("data-mq-block");
    if (blockId == null || blockId.length === 0) return null;

    const group = writable.closest<HTMLElement>("[data-mq-footer-group]")?.getAttribute("data-mq-footer-group") ?? null;
    const selector = group == null
      ? `[data-mq-block="${blockId}"].mq-block-writable`
      : `[data-mq-block="${blockId}"] [data-mq-footer-group="${group}"] .mq-block-writable`;
    // Solange die Fläche im Dokument hängt, ist sie das Ziel; erst nach einem Neuaufbau des
    // Blattes wird sie wieder gesucht.
    const element = writable.isConnected ? writable : doc.querySelector<HTMLElement>(selector);
    if (element == null) return null;

    return createInlineTextTarget(element, () => {
      onTextChange(blockId, systemDocumentHtmlToValue(documentBlockTextSource(element)), group == null ? null : Number(group));
    });
  }, [onTextChange, writable]);

  /** Stabile Schreibfläche für die Werkzeugleiste; sie löst sich erst beim Aufruf auf. */
  const editorTarget = React.useMemo<TemplateVariableEditorTarget>(() => ({
    insert: (value) => resolveLiveTarget()?.insert(value),
    tokenAtCaret: () => resolveLiveTarget()?.tokenAtCaret() ?? null,
    replaceToken: (token, next) => resolveLiveTarget()?.replaceToken(token, next),
  }), [resolveLiveTarget]);

  const selectedIdRef = React.useRef(selectedId);
  React.useEffect(() => {
    selectedIdRef.current = selectedId;
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;
    doc.querySelectorAll(".mq-block-selected").forEach((node) => node.classList.remove("mq-block-selected"));
    if (selectedId != null) {
      doc.querySelector(`[data-mq-block="${CSS.escape(selectedId)}"]`)?.classList.add("mq-block-selected");
    }
  }, [frameRef, selectedId]);

  /**
   * Was sich bei jedem Tastendruck ändert, liegt in einer Ref — nicht in den Abhängigkeiten der
   * Fläche.
   *
   * Sonst wird die Bedienung mitten im Schreiben neu eingerichtet: Der Fokus springt heraus, die
   * Schreibmarke geht verloren, und das gerade Getippte steht im Dokument neben einem gerade neu
   * gesetzten `contentEditable`. Genau daran scheiterte das Editieren.
   */
  // Beim ersten Fokus wird genau das bereits geladene srcDoc eingefroren. Zuvor wurde nur das
  // Server-Markup ohne Vorschau-Marker gespeichert; allein dieser Zeichenunterschied lud den
  // iframe neu und nahm dem gerade markierten Text sofort wieder Auswahl und Fokus.
  const livePreviewHtmlRef = React.useRef(livePreviewHtml);
  const liveRef = React.useRef({
    blocks,
    onReorder,
    onLogoPosition,
    onRemoveBlock,
    onTextChange,
    onToggleVisible,
    onAddBlock,
    isBlockAllowed,
    zoom,
    previewValuesEnabled: effectivePreviewValuesEnabled,
    previewValueSource,
    previewTemplateValues,
  });
  React.useEffect(() => {
    liveRef.current = {
      blocks,
      onReorder,
      onLogoPosition,
      onRemoveBlock,
      onTextChange,
      onToggleVisible,
      onAddBlock,
      isBlockAllowed,
      zoom,
      previewValuesEnabled: effectivePreviewValuesEnabled,
      previewValueSource,
      previewTemplateValues,
    };
    livePreviewHtmlRef.current = livePreviewHtml;
  }, [blocks, effectivePreviewValuesEnabled, isBlockAllowed, livePreviewHtml, onAddBlock, onLogoPosition, onRemoveBlock, onReorder, onTextChange, onToggleVisible, previewTemplateValues, previewValueSource, zoom]);

  const applyPreviewModeToDocument = React.useCallback((doc: Document) => {
    const { previewTemplateValues, previewValueSource, previewValuesEnabled } = liveRef.current;
    if (previewValueSource == null) return;

    const applyPreviewMode = (element: HTMLElement, source: string | null) => {
      if (source == null) return;
      if (previewValuesEnabled) {
        if (previewTemplateValues != null) applyTemplateValuePreview(element, source, previewTemplateValues);
      } else {
        applyTemplateSource(element, source);
      }
    };

    doc.querySelectorAll<HTMLElement>("[data-mq-field]").forEach((element) => {
      const blockId = element.closest<HTMLElement>("[data-mq-block]")?.getAttribute("data-mq-block") ?? "";
      const source = previewValueSource(blockId, element.getAttribute("data-mq-field"), null)
        ?? systemDocumentHtmlToValue(element);
      applyPreviewMode(element, source);
    });
    doc.querySelectorAll<HTMLElement>("[data-mq-block]").forEach((element) => {
      if (element.querySelector("[data-mq-field], .mq-block-affix") != null) return;
      const blockId = element.getAttribute("data-mq-block") ?? "";
      const footerGroups = element.querySelectorAll<HTMLElement>("[data-mq-footer-group]");
      if (footerGroups.length > 0) {
        footerGroups.forEach((group) => {
          const groupIndex = Number(group.getAttribute("data-mq-footer-group"));
          const source = previewValueSource(blockId, null, Number.isFinite(groupIndex) ? groupIndex : null)
            ?? systemDocumentHtmlToValue(group);
          applyPreviewMode(group, source);
        });
        return;
      }
      const source = previewValueSource(blockId, null, null) ?? systemDocumentHtmlToValue(element);
      applyPreviewMode(element, source);
    });
  }, []);

  // `srcDoc` und die Musterwerte treffen asynchron ein. Die erneute Anwendung nach einer
  // Wertantwort schließt das Zeitfenster, in dem das iframe schon geladen ist, aber noch das
  // vorherige (leere) Vorschauwert-Set gesehen hat.
  React.useEffect(() => {
    const doc = frameRef.current?.contentDocument;
    if (doc != null) applyPreviewModeToDocument(doc);
  }, [applyPreviewModeToDocument, effectivePreviewValuesEnabled, previewTemplateValues]);

  const activate = React.useCallback((doc: Document) => {
    // Nach jedem Neuaufbau des Blattes ist der Körper ein anderer — die Vergrößerung muss mit.
    if (doc.body != null) doc.body.style.zoom = String(liveRef.current.zoom);
    applyPreviewModeToDocument(doc);
    const canvasCleanup = activateDocumentBlockCanvas(doc, {
      blocks: liveRef.current.blocks,
      mode,
      selectedBlockId: selectedIdRef.current,
      emptyHint: text("Write text"),
      onSelect: (blockId, _blockLevel, page) => {
        setSelectedId(blockId);
        if (page != null) setSelectedPage(page);
        if (blockId == null) setSettingsOpen(false);
      },
      onWritableFocus: (element) => {
        setWritable(element);
        reportWriting(element != null);
        // Beim Betreten der Schreibfläche wird das Blatt eingefroren, beim Verlassen wieder
        // nachgeführt, sobald der Server tatsächlich einen neuen Stand liefert.
        if (element == null) {
          setFrozenPreview((current) => current == null ? null : { ...current, releaseOnChange: true });
        } else {
          setFrozenPreview((current) => current != null && !current.releaseOnChange
            ? current
            : { html: livePreviewHtmlRef.current, releaseOnChange: false });
        }
      },
      onReorder: (blockId, overId, placement, zone) => liveRef.current.onReorder?.(blockId, overId, placement, zone),
      onLogoPosition: (blockId, position) => liveRef.current.onLogoPosition?.(blockId, position),
      onRemove: (blockId, page) => liveRef.current.onRemoveBlock?.(blockId, page ?? selectedPageRef.current),
      onDropAddBlock: (type, target) => liveRef.current.onAddBlock?.(type, target),
      isBlockAllowed: (type, sheet) => liveRef.current.isBlockAllowed?.(type, sheet) ?? true,
      onToggleVisible: (blockId, visible, page) => liveRef.current.onToggleVisible?.(blockId, visible, page ?? selectedPageRef.current),
      onTextChange: (blockId, element, footerGroup) => {
        liveRef.current.onTextChange(blockId, systemDocumentHtmlToValue(documentBlockTextSource(element)), footerGroup);
      },
      // Nur `mode` und `text` gehören hierher. Alles, was sich beim Tippen ändert, liegt in `liveRef`
      // — sonst wird die Bedienung bei jedem Zeichen neu aufgesetzt und der Fokus springt heraus.
    });
    const contextCleanup = onPreviewDocumentReady?.(doc, () => {
      setSelectedId(null);
      setSettingsOpen(false);
    });
    return () => {
      canvasCleanup();
      contextCleanup?.();
    };
  }, [applyPreviewModeToDocument, mode, onPreviewDocumentReady, reportWriting, text]);

  /**
   * Meldet den Text der Schreibfläche zurück, nachdem ein Editorkommando ihn verändert hat.
   */
  const emitWritten = (element: HTMLElement) => {
    // Zielbaustein und Fußzeilengruppe kommen aus dem geschriebenen Element selbst, nicht aus der
    // Auswahl: Die Auswahl kann auf einem anderen Baustein stehen oder leer sein — dann landete der
    // Text am falschen Baustein oder ging verloren. Beim Fußtext trägt die Zelle die Gruppe, nicht
    // der beschreibbare Bereich in ihr.
    const owner = element.closest<HTMLElement>("[data-mq-block]");
    const blockId = owner?.getAttribute("data-mq-block");
    if (blockId == null || blockId.length === 0) return;

    const group = element.closest<HTMLElement>("[data-mq-footer-group]")?.getAttribute("data-mq-footer-group");
    onTextChange(blockId, systemDocumentHtmlToValue(documentBlockTextSource(element)), group == null ? null : Number(group));
  };

  /**
   * Auszeichnung und Farbe wirken auf die **Markierung**, sobald geschrieben wird — sonst auf den
   * ganzen Baustein.
   *
   * Ohne diese Unterscheidung färbt der Griff nach dem Farbfeld den gesamten Absatz um, obwohl nur
   * ein Wort markiert war. Der Baustein trägt weiterhin die Vorgabe für alles, was kein eigenes
   * Format hat.
   */
  const runInlineCommand = (command: string, value?: string | null) => {
    const element = writable;
    if (element == null || !element.isContentEditable) return false;
    // Ohne markierten Text gibt es nichts auszuzeichnen. Ein Editorkommando setzte dann nur den
    // Zustand für das nächste getippte Zeichen — im Dokument änderte sich nichts, und weil das
    // Kommando trotzdem als erledigt galt, blieb auch die Auszeichnung des Bausteins aus. Genau
    // das ließ die Leiste wirkungslos erscheinen.
    if (!hasInlineSelection(element)) return false;
    // Die Markierung zuerst zurückholen: Der Weg über die Werkzeugleiste nimmt der Fläche den
    // Fokus, und ohne sie färbte das Kommando nichts oder den ganzen Absatz.
    restoreInlineSelection(element);
    executeInlineCommand(element, command, value);
    emitWritten(element);
    return true;
  };

  const runInlineFontFamily = (value: string | null) => {
    const element = writable;
    if (element == null || !element.isContentEditable) return false;
    if (!hasInlineSelection(element)) return false;
    const ok = applyInlineFontFamily(element, value);
    if (ok) emitWritten(element);
    return ok;
  };

  const runInlineFontSize = (value: number | null) => {
    const element = writable;
    if (element == null || !element.isContentEditable) return false;
    if (!hasInlineSelection(element)) return false;
    const ok = applyInlineFontSize(element, value);
    if (ok) emitWritten(element);
    return ok;
  };

  const patchFormat = (patch: Record<string, unknown>) => {
    if (selected != null) onPatchBlock(selected.id, patch);
  };

  // Der Kanal gehört zum Dokumentvertrag. Auch wenn nur eine Oberfläche erlaubt ist, bleibt die
  // Auswahl sichtbar: Die Werkzeugleiste behält ihre Lage und macht den festen Kanal eindeutig.
  const isFixedLetterOrEmailSurface = surfaces.length === 1
    && (surfaces[0] === "letter" || surfaces[0] === "email");
  const visibleSurfaces: DocumentEditorSurface[] = isFixedLetterOrEmailSurface
    ? ["email", "letter"]
    : surfaces;

  return (
    <>
      <Card size="small">
        <Flex vertical gap={spacing.sm}>
          <Flex gap={spacing.sm} wrap align="center" justify="space-between">
            <Flex gap={spacing.sm} align="center">
              {visibleSurfaces.length > 1 ? (
                <Segmented
                  value={surface}
                  disabled={isFixedLetterOrEmailSurface}
                  onChange={(value) => {
                    exitEditing();
                    onSurfaceChange(value as DocumentEditorSurface);
                  }}
                  // E-Mail zuerst: Sie ist der Regelfall, der Brief die Ausnahme.
                  options={visibleSurfaces.map((value) => ({
                    value,
                    label: text(value === "letter"
                      ? "Letter"
                      : value === "email"
                        ? "Email"
                        : "WhatsApp"),
                  }))}
                />
              ) : null}
              {surfaceExtra}
            </Flex>
            <Flex gap={spacing.sm} wrap align="center">
              {/* Woran gearbeitet wird — der Schalter bestimmt, was ein Klick auf dem Blatt tut. */}
              {allowDesign && !staticPreview ? (
                <Segmented
                  value={mode}
                  onChange={(value) => {
                    exitEditing();
                    setDesignMode(value as "design" | "text");
                  }}
                  options={[
                    { value: "design", label: text("Design") },
                    { value: "text", label: text("Text") },
                  ]}
                />
              ) : null}
              <Space size={spacing.xs}>
                <Tooltip title={text("Undo")}>
                  <Button icon={<UndoOutlined />} aria-label={text("Undo")} disabled={!canUndo} onClick={onUndo} />
                </Tooltip>
                <Tooltip title={text("Redo")}>
                  <Button icon={<RedoOutlined />} aria-label={text("Redo")} disabled={!canRedo} onClick={onRedo} />
                </Tooltip>
              </Space>
              {!customSurfacePreview ? (
                <>
                  <DocumentZoomControl
                    zoom={zoom}
                    onZoomChange={applyZoom}
                    zoomSteps={ZOOM_STEPS}
                    showReset
                    onReset={() => applyZoom(1)}
                  />
                  <label className="communication-composer-preview-toggle">
                    <Switch
                      checked={effectivePreviewValuesEnabled}
                      onChange={(checked) => {
                        exitEditing();
                        onPreviewValuesChange?.(checked);
                      }}
                    />
                    <span>{text("Preview values")}</span>
                  </label>
                </>
              ) : null}
              {/* Einstellungen und Speichern kommen aus dem Rahmen des Editors und stehen rechts. */}
              {headerExtra}
            </Flex>
          </Flex>

          <Divider style={{ margin: 0 }} />

          {/*
            Eine Zeile, drei Zustände: ohne Auswahl gibt es nur etwas hinzuzufügen, mit Auswahl die
            Einstellungen des Bausteins, nach dem Doppelklick sein Text. So steht nie eine Bedienung
            da, die gerade nichts trifft.
          */}
          {/*
            Feste Höhe: Die drei Zustände tragen verschieden viele Bedienelemente, und eine Zeile,
            die dabei ihre Höhe ändert, schiebt das Blatt darunter bei jedem Klick hoch und runter.
          */}
          {/*
            `height` statt `minHeight`: Eine Mindesthöhe verhindert nur das Schrumpfen. Der
            Textmodus trägt eine Bedienung mehr als die Gestaltung, wuchs damit über die Zeile
            hinaus — und beim Umschalten sprang der ganze Kopf samt Blatt.
          */}
          <div style={{ display: "flex", alignItems: "center", height: TOOLBAR_ROW_HEIGHT, width: "100%" }}>
          {mode === "text" ? (
            <Flex gap={spacing.xs} align="center" justify="space-between" style={{ width: "100%", minWidth: 0 }}>
              {/* Die Leiste bleibt in schmalen Fenstern schiebbar — nur ihr Balken entfällt, er
                  läge sonst als zweiter Streifen direkt über dem Blatt. */}
              <Flex className="mu-scroll-hidden" gap={spacing.xs} align="center" style={{ flex: "1 1 auto", minWidth: 0, overflowX: "auto" }}>
                <TextFormatToolbar
                  colorResetKey={selected?.id ?? "none"}
                  marksDisabled={selected == null || !selected.formattable}
                  marks={selected?.formattable ? selected.format : {}}
                  onToggleMark={(key: TextFormatMarkKey, active) => {
                    if (!runInlineCommand(key)) patchFormat({ [key]: !active });
                  }}
                  fontOptions={fontOptions}
                  fontFamily={selected?.format.fontFamily ?? null}
                  onFontFamilyChange={(value) => {
                    if (!runInlineFontFamily(value)) patchFormat({ fontFamily: value });
                  }}
                  fontSizePt={selected?.format.fontSizePt ?? null}
                  onFontSizeChange={(value) => {
                    if (!runInlineFontSize(value)) patchFormat({ fontSizePt: value });
                  }}
                  colorsDisabled={selected == null || !selected.formattable}
                  textColor={selected?.format.textColor ?? null}
                  backgroundColor={selected?.format.backgroundColor ?? null}
                  onTextColorChange={(value) => {
                    if (!applyInlineTextColor(writable, value)) patchFormat({ textColor: value });
                    else if (writable != null) emitWritten(writable);
                  }}
                  onBackgroundColorChange={(value) => {
                    if (!applyInlineBackgroundColor(writable, value)) patchFormat({ backgroundColor: value });
                    else if (writable != null) emitWritten(writable);
                  }}
                  alignment={selected?.format.alignment ?? 0}
                  alignmentDisabled={selected == null || !selected.formattable}
                  onAlignmentChange={(value) => patchFormat({ textAlignment: value })}
                />
                <TemplateVariableToolbar
                  compact
                  variables={variables}
                  target={editorTarget}
                  disabled={writable == null}
                />
              </Flex>
              <Flex gap={spacing.xs} align="center" style={{ flex: "0 0 auto", marginLeft: spacing.xs }}>
                {selected != null && renderSettings != null ? (
                  <Tooltip title={text("Settings")}>
                    <Button
                      type="text"
                      icon={<SettingOutlined />}
                      aria-label={text("Settings")}
                      onClick={() => setSettingsOpen(true)}
                    />
                  </Tooltip>
                ) : null}
                {selected != null ? renderTextBlockActions?.(selected.id, selectedPage) : null}
              </Flex>
            </Flex>
          ) : documentContextToolbar != null ? (
            documentContextToolbar
          ) : selected != null && renderBlockToolbar != null ? (
            renderBlockToolbar(selected.id, () => setSettingsOpen(true), selectedPage)
          ) : (
            <Flex gap={spacing.xs} align="center" justify="flex-end" style={{ width: "100%" }}>
              {documentActions}
            </Flex>
          )}
          </div>
        </Flex>
      </Card>

      {/*
        Das Blatt scrollt in seinem eigenen Bereich, nicht die Seite: Sonst wandern Kopfzeile und
        Werkzeugleiste nach oben aus dem Bild — genau dann, wenn man unten auf dem Blatt arbeitet
        und sie braucht.
      */}
      <div
        ref={previewViewportRef}
        style={{
          marginTop: spacing.sm,
          display: "flex",
          gap: spacing.md,
          alignItems: "flex-start",
          width: "100%",
        }}
      >
        {addableBlocks != null && addableBlocks.length > 0 && allowDesign !== false && mode === "design" ? (
          <DocumentBlockPalette
            items={addableBlocks}
            onAddBlock={(type) => onAddBlock?.(type, { sheet: selectedPage ?? 1 })}
            surface={surface === "letter" ? "letter" : "email"}
            maxHeight={previewViewportHeight}
          />
        ) : null}

        <div
          /*
            Der Balken am Blatt bleibt sichtbar: Er ist hier die einzige Anzeige, wie weit das
            Dokument reicht und wo man darin steht.
          */
          style={{
            flex: 1,
            minWidth: 0,
            maxHeight: previewViewportHeight ?? undefined,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <LoadingOverlay visible={loading} scope="block">
            {customPreview ?? (
              <DocumentPreviewFrame
                ref={frameRef}
                title={text(surface === "letter" ? "Letter preview" : "Email preview")}
                html={renderedHtml}
                surface={surface === "whatsapp" ? "email" : surface}
                autoHeight
                scrolling="no"
                onDocumentReady={activate}
                style={{
                  width: "100%",
                  minHeight: surface === "letter" ? FRAME_MIN_HEIGHT : undefined,
                  border: 0,
                  display: "block",
                }}
              />
            )}
          </LoadingOverlay>
        </div>
      </div>

      {renderSettings != null && selected != null && settingsOpen
        ? renderSettings(selected.id, () => setSettingsOpen(false), selectedPage)
        : null}
    </>
  );
}
